import { useRouter } from 'next/router'
import { useQuery } from '@tanstack/react-query'
import { calculateDefaultPayment, calculateDefaultPaymentKey } from '../../api-client/purchase'
import { getAuctionInfo, getAuctionInfoKey } from '../../api-client/auction'
import { getBuyNowListingInfo, getBuyNowListingInfoKey } from '../../api-client/listing'
import { getVehicle, getVehicleKey } from '../../api-client/search'
import { ListingType, UserType } from '../../api-client/enums'
import { useMemo, useState, useEffect } from 'react'
import { getCardDetails, getCardDetailsKey } from '../../api-client/account'
import { PhotoType } from '../../api-client/enums/photo-type'
import { calculateShipmentRate, calculateShipmentRateKey } from '../../api-client/purchase'
import { triggerFullIntegrationVehicleIngestion } from '../../api-client/integration'
import { useAuth } from '../../contexts/auth'

export default function useVehiclePageData() {
  const {
    query: { vehicleId },
  } = useRouter()
  const [auctionInfo, setAuctionInfo] = useState(null)
  const [sellerInfo, setSellerInfo] = useState({})
  const [isIngestionComplete, setIsIngestionComplete] = useState(false)
  const [isVehicleDataLoading, setIsVehicleDataLoading] = useState(true)
  const [auctionInfoPollingEnabled, setAuctionInfoPollingEnabled] = useState(false)
  const [refetchInterval, setRefetchInterval] = useState(1000)
  const [shipmentRate, setShipmentRate] = useState({})
  const [{ isAuthenticated }] = useAuth()

  const { data: ingestionData, isLoading: isIngestionLoading } = useQuery({
    queryKey: ['triggerFullIntegrationVehicleIngestion', vehicleId],
    queryFn: () => triggerFullIntegrationVehicleIngestion({ vehicleId }),
    enabled: !!vehicleId,
  })

  useEffect(() => {
    if (!vehicleId) return
    if (isIngestionLoading) return
    if (ingestionData === undefined) return

    setIsIngestionComplete(true)
  }, [vehicleId, isIngestionLoading, ingestionData])

  const { data: vehicleData, isLoading: isGetVehicleDataLoading } = useQuery({
    queryKey: [getVehicleKey, vehicleId],
    enabled: isIngestionComplete,
    queryFn: () => getVehicle({ vehicleId }),
  })

  useEffect(() => {
    if (!isIngestionComplete) {
      return
    }
    if (isGetVehicleDataLoading) {
      return
    }
    setIsVehicleDataLoading(false)
  }, [isIngestionComplete, isGetVehicleDataLoading])

  const { data: auctionInfoData, isLoading: isAuctionInfoLoading } = useQuery({
    queryKey: [getAuctionInfoKey, vehicleData?.auctionId],
    queryFn: () => getAuctionInfo({ auctionId: vehicleData?.auctionId }),
    enabled: vehicleData?.listingType === ListingType.Auction,
  })

  useEffect(() => {
    if (!auctionInfoData) return
    setAuctionInfo(auctionInfoData)
  }, [auctionInfoData])

  // Polling auction info
  const { data: pollingAuctionInfoData } = useQuery({
    queryKey: [getAuctionInfoKey, vehicleData?.auctionId, 'polling'],
    queryFn: () => getAuctionInfo({ auctionId: vehicleData?.auctionId }),
    enabled: auctionInfoPollingEnabled,
    refetchInterval: refetchInterval,
  })

  useEffect(() => {
    if (!pollingAuctionInfoData) return

    if (pollingAuctionInfoData?.auctionStatus !== auctionInfo?.auctionStatus) {
      setAuctionInfo(pollingAuctionInfoData)
      setAuctionInfoPollingEnabled(false)
    }

    setRefetchInterval((prev) => prev * 2)
  }, [pollingAuctionInfoData, auctionInfo?.auctionStatus])

  const { data: buyNowListingInfo, isLoading: isBuyNowListingInfoLoading } = useQuery({
    queryKey: [getBuyNowListingInfoKey, vehicleData?.buyNowListingId],
    queryFn: () => getBuyNowListingInfo({ buyNowListingId: vehicleData?.buyNowListingId }),
    enabled: isIngestionComplete && vehicleData?.listingType === ListingType.BuyNow,
  })

  const { data: paymentInfo, isLoading: isPaymentInfoLoading } = useQuery({
    queryKey: [
      calculateDefaultPaymentKey,
      vehicleId,
      auctionInfo?.topBidAmount,
      vehicleData?.desiredBuyNowPrice,
    ],
    queryFn: () =>
      calculateDefaultPayment({
        vehicleId: vehicleId,
        price: auctionInfo?.topBidAmount || vehicleData?.desiredBuyNowPrice || 0,
      }),
    enabled: isIngestionComplete,
  })

  const { data: sellerInfoData, isLoading: isSellerInfoLoading } = useQuery({
    queryKey: [getCardDetailsKey, vehicleData?.sellerId],
    queryFn: () => getCardDetails({ userId: vehicleData?.sellerId, returnVerifiedDealer: true }),
    enabled: isIngestionComplete && !!vehicleData?.sellerId,
  })

  useEffect(() => {
    if (!sellerInfoData) return

    const isDealer = sellerInfoData?.userData?.accountUserType === UserType.Dealer

    setSellerInfo({
      ...sellerInfoData,
      isDealer,
      displayName: sellerInfoData.firstName,
    })
  }, [sellerInfoData])

  const { data: shipmentRateData, isLoading: isShipmentRateLoading } = useQuery({
    queryKey: [calculateShipmentRateKey, vehicleId],
    enabled: isAuthenticated && isIngestionComplete && !!vehicleId,
    queryFn: () =>
      calculateShipmentRate({
        vehicleId: vehicleId,
      }),
  })

  useEffect(() => {
    if (!shipmentRateData) return
    setShipmentRate({ ...shipmentRateData })
  }, [shipmentRateData])

  const galleryImages = useMemo(() => {
    return [
      ...(vehicleData?.mainPhotos.map((item) => {
        return { ...item, photoType: PhotoType.MainPhoto }
      }) || []),
      ...(vehicleData?.untaggedPhotos.map((item) => {
        return { ...item, photoType: PhotoType.UntaggedPhoto }
      }) || []),
    ]
  }, [vehicleData])

  const startAuctionInfoPolling = () => {
    setAuctionInfoPollingEnabled(true)
  }

  return {
    vehicleData,
    auctionInfo,
    buyNowListingInfo,
    paymentInfo,
    sellerInfo,
    galleryImages,
    isVehicleDataLoading,
    isAuctionInfoLoading,
    isBuyNowListingInfoLoading,
    isPaymentInfoLoading,
    isSellerInfoLoading,
    auctionInfoPollingEnabled,
    shipmentRate,
    isShipmentRateLoading,
    queryLoadingStatuses: {
      isVehicleDataLoading,
      isAuctionInfoLoading,
      isBuyNowListingInfoLoading,
      isPaymentInfoLoading,
    },
    startAuctionInfoPolling,
  }
}
